import React, { useMemo, useState, useEffect, useRef } from "react";
import Tabs from "../tabs";
import { getIndividualUserTaskThunk } from "../../leadMangementThunk";
import { useDispatch, useSelector } from "react-redux";
import {
    Stack, Box, RadioGroup, FormControl, FormControlLabel,
    Radio, Dialog, DialogContent, Typography, Link
} from "@mui/material";
import DataTableWithCheckbox from "../../../../components/dataTableWithSearch/DataTableWithCheckBox";
import Slide from "@mui/material/Slide";
import GroupIcon from "@mui/icons-material/Group";
import { QR, qrDialogPaperSx, QrModalHeader } from "../../../qrCode/components/qrUi";
import { NameCell, ChipList, MutedText, StatusPill, statusColor } from "../leadUi";
import SnackbarMessage from "../../../../components/snackBar/snackBar";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

// ===== Memoized Count Button =====
const CountButton = React.memo(({ value, status, handleOpen }) => {
    if (!value) return null;
    const c = statusColor(status);
    return (
        <Link
            component="button"
            type="button"
            underline="none"
            title={value.count !== 0 ? `View ${value.count} lead(s)` : undefined}
            sx={{
                pointerEvents: value.count === 0 ? "none" : "auto",
                minWidth: 30,
                px: 1,
                py: 0.25,
                borderRadius: 5,
                border: value.count === 0 ? "none" : `1px solid ${c}40`,
                fontSize: 12.5,
                fontWeight: 800,
                fontFamily: "inherit",
                cursor: "pointer",
                color: value.count === 0 ? "#cbd5e1" : c,
                bgcolor: value.count === 0 ? "transparent" : `${c}14`,
                transition: "all .15s ease",
                "&:hover": { color: "#fff", bgcolor: c },
            }}
            onClick={value.count !== 0 ? () => handleOpen(value.leads, status) : undefined}
        >
            {value.count !== 0 ? value.count : "-"}
        </Link>
    );
});

const pillRadioSx = {
    m: 0,
    pl: 0.5,
    pr: 1.25,
    height: 32,
    borderRadius: 5,
    border: "1px solid #e2e8f0",
    bgcolor: "#fff",
    "& .MuiRadio-root": { p: 0.75 },
    "& .MuiRadio-root .MuiSvgIcon-root": { fontSize: 18 },
    "& .MuiFormControlLabel-label": { fontSize: 13, fontWeight: 600, color: "#475569" },
    "&:has(.Mui-checked)": {
        borderColor: "#2563eb",
        bgcolor: "#2563eb0d",
        "& .MuiRadio-root": { color: "#2563eb" },
        "& .MuiFormControlLabel-label": { color: "#2563eb", fontWeight: 700 },
    },
};

export default function MyTeamTask() {
    const dispatch = useDispatch();
    const scrollRef = useRef(null);

    const { getIndividualUserTaskDetails = [], status, error } = useSelector(
        (state) => state.leadManagement
    );

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [selectedRadioOption, setSelectedRadioOption] = useState("1");

    // ===== Combined modal state =====
    const [modalState, setModalState] = useState({
        open: false,
        selectedLeads: [],
        statusTitle: "",
    });

    const handleOpen = (leads, status) => {
        setModalState({ open: true, selectedLeads: leads, statusTitle: status });
    };
    const handleClose = () =>
        setModalState((prev) => ({ ...prev, open: false }));

    const handleOptionChange = (event) => {
        setSelectedRadioOption(event.target.value);
    };

    // ===== Transform data into table rows =====
    const rows = useMemo(() => {
        const users = getIndividualUserTaskDetails?.[0]?.user_details ?? [];
        return users.map((user, idx) => {
            const base = {
                sno: idx + 1,
                employee_name: user.employee_name,
                emp_email: user.emp_email,
                employee_type: user.employee_type,
                branch_name: user.branch_name,
                department_name: user.department_name,
            };
            user.lead_status_details.forEach((status) => {
                base[status.lead_status_type] = {
                    count: status.count,
                    leads: status.leads,
                };
            });
            return base;
        });
    }, [getIndividualUserTaskDetails]);

    // ===== Prepare table columns =====
    const columns = useMemo(() => {
        const baseCols = [
            { header: "SNO", accessorKey: "sno" },
            { header: "Name", accessorKey: "employee_name", Cell: ({ row }) => <NameCell name={row.original.employee_name} /> },
            { header: "Email", accessorKey: "emp_email", Cell: ({ row }) => <MutedText value={row.original.emp_email} /> },
            { header: "Employee Type", accessorKey: "employee_type", Cell: ({ row }) => <ChipList value={row.original.employee_type} color="#0891b2" max={1} /> },
            { header: "Branch", accessorKey: "branch_name", Cell: ({ row }) => <MutedText value={row.original.branch_name} /> },
            { header: "Department", accessorKey: "department_name", Cell: ({ row }) => <ChipList value={row.original.department_name} color={QR.blue} /> },
        ];

        const firstUser = getIndividualUserTaskDetails?.[0]?.user_details?.[0];
        const statusNames = firstUser?.lead_status_details?.map(
            (s) => s.lead_status_type
        ) ?? [];

        const statusCols = statusNames.map((status) => ({
            header: status,
            accessorKey: status,
            Cell: ({ cell }) => (
                <CountButton value={cell.getValue()} status={status} handleOpen={handleOpen} />
            ),
        }));

        return [...baseCols, ...statusCols];
    }, [getIndividualUserTaskDetails]);

    useEffect(() => {
        dispatch(getIndividualUserTaskThunk());
    }, [dispatch]);

    // Optional snackbar for errors
    useEffect(() => {
        if (status === "failed")
            setSnackbar({ open: true, message: error, severity: "error" });
    }, [status, error]);



    const leadColumns = useMemo(
        () => [
            { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
            { accessorKey: "contact_name", header: "Name", Cell: ({ row }) => <NameCell name={row.original.contact_name} /> },
            { accessorKey: "email", header: "Email", Cell: ({ row }) => <MutedText value={row.original.email} /> },
            { accessorKey: "mobile", header: "Mobile", Cell: ({ row }) => <MutedText value={row.original.mobile} /> },
            { accessorKey: "lead_status", header: "Status", Cell: ({ row }) => <StatusPill value={row.original.lead_status} /> },
            { accessorKey: "department_names", header: "Department", Cell: ({ row }) => <ChipList value={row.original.department_names} color={QR.blue} /> },
            { accessorKey: "branch_names", header: "Branch", Cell: ({ row }) => <MutedText value={row.original.branch_names} /> },
            { accessorKey: "tag_names", header: "Tag", Cell: ({ row }) => <ChipList value={row.original.tag_names} /> },
        ], []
    );

    return (
        <div>
            <Tabs>
                {/* ---------- Overview header: title + view ---------- */}
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    spacing={1.25}
                    sx={{ mt: 1.5 }}
                >
                    <Box>
                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a", lineHeight: 1.3 }}>Team overview</Typography>
                        <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>Lead status of everyone in your team</Typography>
                    </Box>
                    <FormControl component="fieldset">
                        <RadioGroup
                            aria-label="user-type"
                            name="user-type"
                            value={selectedRadioOption}
                            onChange={handleOptionChange}
                            row
                            sx={{ gap: 1 }}
                        >
                            <FormControlLabel value="1" control={<Radio />} label={<Stack direction="row" spacing={0.6} alignItems="center"><GroupIcon sx={{ fontSize: 16 }} /><span>Individual User</span></Stack>} sx={pillRadioSx} />
                            {/* <FormControlLabel value="2" control={<Radio />} label="Level Wise User" /> */}
                        </RadioGroup>
                    </FormControl>
                </Stack>

                {/* ---------- Summary cards ---------- */}
                <Box
                    ref={scrollRef}
                    sx={{
                        mt: 1.5,
                        mb: 1,
                        pb: 0.5,
                        // all cards on one line, each as wide as its full name; scrolls sideways only if needed
                        display: "flex",
                        flexWrap: "nowrap",
                        gap: 1,
                        overflowX: "auto",
                        "&::-webkit-scrollbar": { height: 6 },
                        "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 3 },
                    }}
                >
                    {[
                        { label: "Team Members", count: rows.length, c: QR.blue, icon: true },
                        ...(getIndividualUserTaskDetails?.[0]?.status_details || []).map((item) => ({
                            label: item.lead_status_type,
                            count: item.count,
                            c: statusColor(item.lead_status_type),
                        })),
                    ].map((card, idx) => (
                        <Stack
                            key={idx}
                            direction="row"
                            alignItems="center"
                            spacing={0.9}
                            title={`${card.label}: ${card.count}`}
                            sx={{
                                flex: "1 0 auto", // grow to fill the row, never shrink below the text
                                py: 0.85,
                                px: 1.1,
                                borderRadius: 2.5,
                                bgcolor: card.icon ? `${card.c}0a` : "#fff",
                                border: `1px solid ${card.icon ? `${card.c}33` : QR.border}`,
                                borderLeft: `3px solid ${card.c}`,
                                boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
                            }}
                        >
                            {card.icon ? (
                                <Box sx={{ width: 26, height: 26, flexShrink: 0, borderRadius: 1.75, display: "flex", alignItems: "center", justifyContent: "center", color: card.c, bgcolor: `${card.c}14` }}>
                                    <GroupIcon sx={{ fontSize: 15 }} />
                                </Box>
                            ) : (
                                <Box sx={{ width: 7, height: 7, flexShrink: 0, borderRadius: "50%", bgcolor: card.c, boxShadow: `0 0 0 3px ${card.c}22` }} />
                            )}
                            <Box>
                                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "#64748b", lineHeight: 1.3, whiteSpace: "nowrap" }}>
                                    {card.label}
                                </Typography>
                                <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                                    {card.count}
                                </Typography>
                            </Box>
                        </Stack>
                    ))}
                </Box>

                {selectedRadioOption === "1" ? (
                    <DataTableWithCheckbox
                        columns={columns}
                        status={status}
                        data={rows}
                        enableRowSelection={false}
                        searchFirst
                        emptyMessage="No team members found."
                    />
                ) : (
                    <h1>nnn</h1>
                )
                }

                {/* Leads modal */}
                <Dialog
                    open={modalState.open}
                    onClose={handleClose}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="md"
                    keepMounted
                    PaperProps={{ sx: qrDialogPaperSx }}
                >
                    <QrModalHeader
                        icon={<GroupIcon />}
                        title={`${modalState.statusTitle} - Leads`}
                        subtitle={`${(modalState.selectedLeads || []).length} lead(s)`}
                        onClose={() => handleClose()}
                    />
                    <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                        <DataTableWithCheckbox
                            status={status}
                            columns={leadColumns}
                            data={modalState.selectedLeads || []}
                            enableRowSelection={false}
                            searchFirst
                            emptyMessage="No leads."
                        />
                    </DialogContent>
                </Dialog>
            </Tabs>
            <SnackbarMessage open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} />
        </div>
    );
}
